/**
 * CurrentShiftBadge — header pill showing cashier's active shift.
 *
 * Two states:
 *   • No active shift → green "Open shift" button → ShiftOpenDialog
 *   • Active shift → navy pill showing shift number + elapsed time →
 *     click opens ShiftCloseDialog
 *
 * Drop into cashier Header (or admin Header for non-cashier views with
 * `forCashier=false`).
 *
 * Day 14 Sprint 1D #3 + #4.
 */
import { useState, useEffect } from "react";
import { Clock, PlayCircle, StopCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCurrentShift } from "@/hooks/useShifts";
import { useLocation } from "react-router-dom";
import { useEffectiveRole } from "@/components/auth/RouteGuards";
import { usePermissions } from "@/hooks/usePermissions";
import ShiftOpenDialog from "@/components/shifts/ShiftOpenDialog";
import ShiftCloseDialog from "@/components/shifts/ShiftCloseDialog";

function elapsedString(start: string): string {
  const ms = Date.now() - new Date(start).getTime();
  const totalMin = Math.floor(ms / 60000);
  const hr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (hr === 0) return `${min}m`;
  return `${hr}h ${min}m`;
}

export default function CurrentShiftBadge() {
  // Cashiers always see their shift; anyone else does while they're on the
  // till (a manager stepping in opens and closes their own drawer too).
  const role = useEffectiveRole();
  const { pathname } = useLocation();
  const { can } = usePermissions();
  const isCashier = role === "cashier" || (pathname.startsWith("/cashier") && can("shift.open"));
  const { data: shift, isLoading } = useCurrentShift(undefined, isCashier);

  // Tick every 60s so elapsed time stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!shift) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [shift]);

  const [openOpen, setOpenOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  if (!isCashier) return null;
  if (isLoading) return null;

  // ── No active shift ──
  if (!shift) {
    return (
      <>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpenOpen(true)}
          className="h-8 text-xs gap-1.5 text-[#4CAF50] hover:bg-[#4CAF50]/10 hover:text-[#45A049]"
        >
          <PlayCircle className="h-3.5 w-3.5" />
          Open shift
        </Button>
        <ShiftOpenDialog open={openOpen} onOpenChange={setOpenOpen} />
      </>
    );
  }

  // ── Active shift ──
  const longRunning =
    Date.now() - new Date(shift.openedAt).getTime() > 12 * 60 * 60 * 1000;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setCloseOpen(true)}
            className="inline-flex h-8 items-center gap-2 rounded-md border border-[#0A2540]/15 bg-[#0A2540]/5 px-2.5 text-xs font-medium text-[#0A2540] transition-colors hover:bg-[#0A2540]/10"
            aria-label="View and close current shift"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4CAF50] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#4CAF50]" />
            </span>
            <Clock className="h-3 w-3" />
            <span className="hidden sm:inline">{shift.shiftNumber}</span>
            <span className="text-muted-foreground tabular-nums">
              {elapsedString(shift.openedAt)}
            </span>
            {longRunning && (
              <AlertTriangle className="h-3 w-3 text-amber-600" />
            )}
            <StopCircle className="h-3 w-3 text-[#0A2540]/60 ml-0.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <div className="text-xs font-medium">
            {shift.cashierName} · {shift.shiftNumber}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Opened{" "}
            {new Date(shift.openedAt).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            · click to close
          </div>
        </TooltipContent>
      </Tooltip>
      <ShiftCloseDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        shift={shift}
      />
    </TooltipProvider>
  );
}
